import { useMemo } from 'react'

import { useTranslations } from '../../../translate'

export type ComparatorVariant = 'value' | 'array' | 'date'

export type ValueComparatorType =
  | 'is'
  | 'is_not'
  | 'is_any_of'
  | 'is_part_of'
  | 'is_not_part_of'

export type ArrayComparatorType =
  | 'includes'
  | 'not_includes'
  | 'includes_all_of'
  | 'includes_any_of'
  | 'includes_none_of'
export type DateComparatorType = 'is_after' | 'is_before'

export type ComparatorType =
  | ValueComparatorType
  | ArrayComparatorType
  | DateComparatorType

export type ComparatorOperation = {
  value: ComparatorType
  label: string
  isMulti?: boolean
  isPartOf?: boolean
  isNegation?: boolean
  isEmpty?: boolean
}

const filterOperations = (
  operations: ComparatorOperation[],
  numberOfSelectedItems: number,
  noNegations: boolean,
  showPartOf: boolean,
): ComparatorOperation[] => {
  const showMulti = numberOfSelectedItems > 1
  return operations
    .filter(({ isMulti }) => !!isMulti === showMulti)
    .filter(({ isNegation }) => !isNegation || !noNegations)
    .filter(({ isPartOf }) => !isPartOf || showPartOf)
}

export const useComparatorOperations = (
  type: ComparatorVariant,
  numberOfSelectedItems: number,
  noNegations?: boolean,
  showPartOf?: boolean,
): ComparatorOperation[] => {
  const t = useTranslations()

  return useMemo(() => {
    if (type === 'value') {
      return filterOperations(
        [
          { value: 'is', label: t.ui.is() },
          { value: 'is_not', label: t.ui.is_not(), isNegation: true },
          {
            value: 'is_any_of',
            label: t.ui.is_any_of(),
            isMulti: true,
          },
          {
            value: 'is_part_of',
            label: t.ui.is_part_of(),
            isPartOf: true,
          },
          {
            value: 'is_not_part_of',
            label: t.ui.is_not_part_of(),
            isPartOf: true,
            isNegation: true,
          },
        ],
        numberOfSelectedItems,
        noNegations || false,
        showPartOf || false,
      )
    }

    if (type === 'date') {
      return [
        {
          value: 'is_after',
          label: t.ui.is_after(),
        },
        {
          value: 'is_before',
          label: t.ui.is_before(),
        },
      ]
    }

    return filterOperations(
      [
        {
          value: 'includes',
          label: t.ui.includes(),
        },
        {
          value: 'not_includes',
          label: t.ui.not_includes(),
          isNegation: true,
        },
        {
          value: 'includes_any_of',
          label: t.ui.includes_any_of(),
          isMulti: true,
        },
        {
          value: 'includes_all_of',
          label: t.ui.includes_all_of(),
          isMulti: true,
        },
        {
          value: 'includes_none_of',
          label: t.ui.includes_none_of(),
          isMulti: true,
          isNegation: true,
        },
      ],
      numberOfSelectedItems,
      noNegations || false,
      showPartOf || false,
    )
  }, [type, numberOfSelectedItems])
}
